import { Heading } from '@chakra-ui/layout'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useContractContext } from 'contexts/contract'
import { LocationsDTO } from 'models'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getMunicipio } from 'services/municipio'
import { removeDuplicateCities } from 'utils/removeDuplicateCities'
import isDisableToEditContract from '../../StepsForm/Utils/isDisableToEditContract'
import { FormFinancingProps } from '../FormFinancing/interfaces'
import { FormContractValue } from './form'
import { FormContractProps } from './interfaces'
import { FormContractKeys } from './types'
interface PropsContract {
    instituicaoPagaTaxa: boolean
}

const FormContract = ({ instituicaoPagaTaxa }: PropsContract) => {
    const {
        register,
        watch,
        setValue,
        formState: { errors }
    } = useFormContext<{
        contract: FormContractProps
        financing: FormFinancingProps
    }>()
    const ufContrato = 'contract.ufContrato'
    const municipioContrato = 'contract.municipioContrato'
    const { typeContractBoolean, typeContract } = useContractContext()
    const [searching, setSearching] = useState(false)
    const [location, setLocation] = useState<LocationsDTO[]>([])

    const hasError = (id: FormContractKeys) => {
        return errors.contract && errors.contract[id]
    }

    const aditivo = [
        'dataAditivo',
        'tipoAditivo',
        'numAditivoContratoOrigem',
        'numAditivoContrato',
        'dataAlteracao'
    ]

    const consultMunicipio = async (uf: string) => {
        if (!uf) {
            setLocation([])
            return
        }

        try {
            setSearching(true)

            const { data } = await getMunicipio(uf)
            const cleanedLocations = removeDuplicateCities(data)

            setLocation(cleanedLocations)

            if (
                typeContract !== 'CREATE' &&
                typeContract !== 'EXTERNAL_ADDITIVE'
            ) {
                handleContractMunicipio()
            }
        } catch (error) {
            console.error('Error fetching municipalities:', error)
            setLocation([])
        } finally {
            setSearching(false)
        }
    }

    const handleContractMunicipio = () => {
        const municipio = watch(municipioContrato)

        if (municipio) {
            const normalizedMunicipio = municipio
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')

            setValue('contract.municipioContrato', normalizedMunicipio)
        }
    }

    useEffect(() => {
        consultMunicipio(watch(ufContrato))
    }, [])

    useEffect(() => {
        const subscription = watch(async (_, { name }) => {
            if (name === ufContrato) consultMunicipio(watch(name))
        })
        return () => subscription.unsubscribe()
    }, [watch])

    const form = useMemo(() => {
        if (
            typeContract !== 'ADDITIVE' &&
            typeContract !== 'EDITADDITIVE' &&
            typeContract !== 'EXTERNAL_ADDITIVE'
        ) {
            return FormContractValue(
                watch(ufContrato),
                location,
                typeContract
            ).filter(el => {
                if (!aditivo.includes(el.input?.id as string) &&
                    !aditivo.includes(el.select?.id as string)) {
                    return el
                }
            })
        }

        return FormContractValue(watch(ufContrato), location, typeContract)
    }, [
        watch(ufContrato),
        location,
        watch('contract.municipioContrato'),
        typeContract
    ])

    const disabledInput = (input: any) => {
        return (
            typeContractBoolean &&
            isDisableToEditContract[`contract.${input.id}`]
        )
    }

    const showInput = (input: any) => {
        if (!instituicaoPagaTaxa && input.id === 'numeroDudaPago') return false
        return true
    }

    return (
        <Layout
            type="container"
            px={8}
            full
            display="grid"
            gap={8}
            data-testid="form-container"
        >
            <Layout type="row" data-testid="form-heading">
                <Heading size="md" textAlign={{ sm: 'center' }}>
                    Dados do contrato
                </Heading>
            </Layout>
            <Layout type="row" gutter={{ y: 1.5 }} data-testid="form-fields">
                {form.map(({ layout, input, select }, key) => (
                    <Layout {...layout} key={key}>
                        {select ? (
                            <SelectBuild
                                {...select}
                                err={hasError(select.id)}
                                {...register(`contract.${select.id}`)}
                                isSearching={select.isSearching && searching}
                                value={watch(`contract.${select.id}`) as string}
                                isDisable={
                                    select?.id === 'ufContrato' ||
                                    (typeContractBoolean &&
                                        isDisableToEditContract[
                                            `contract.${select.id}`
                                        ])
                                }
                            />
                        ) : (
                            input &&
                            showInput(input) && (
                                <InputBuild
                                    {...input}
                                    err={hasError(input.id)}
                                    {...register(`contract.${input.id}`)}
                                    value={
                                        watch(`contract.${input.id}`) as string
                                    }
                                    isDisable={disabledInput(input)}
                                />
                            )
                        )}
                    </Layout>
                ))}
            </Layout>
        </Layout>
    )
}

export default FormContract
